import React, { useMemo } from 'react';
import { Button, ButtonVariant } from '@vimily/bonjoro-ui-components-v2/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVideo } from '@fortawesome/free-solid-svg-icons';
import * as BrowserSupport from '~/utils/BrowserSupport';
import { useTranslation } from 'react-i18next';

import { ManualUploadButton } from '@vimily/bonjoro-ui-components-v2/VideoRecorder';
import { computeTextColor } from '@vimily/bonjoro-ui-components-v2';
import { theme } from '@vimily/bonjoro-ui-components/theme';
import styled from '@emotion/styled';
import { colors } from '@vimily/bonjoro-ui-components-v2/dist/theme';

export enum ActiveSection {
  Selector = 'selector',
  VideoRecorder = 'video-recorder',
  TextArea = 'text-area',
  VideoPreview = 'video-preview',
  TextPreviewMobile = 'text-preview-mobile',
}

export interface SelectorProps {
  onResponseTypeClick: (section: ActiveSection) => void;
  onManualUpload: (file: File) => void;
  className?: string;
  hideTextResponseOption?: boolean;
  disabled?: boolean;
  primaryCtaColor?: string;
  secondaryCtaColor?: string;
  ctaCornerRadius?: number;
  fontColor?: string;
  fontFamily?: string;
}

const UploadButtonIcon = styled.div<{color?: string;}>`
  background: ${({color = colors.black}) => `${color} !important`};
`;

export default function Selector({
  onResponseTypeClick,
  onManualUpload,
  className = '',
  hideTextResponseOption = false,
  disabled = false,
  primaryCtaColor,
  secondaryCtaColor,
  ctaCornerRadius = 0,
  fontColor,
  fontFamily,
}: SelectorProps) {

  const handleOnClickResponseType = (section: ActiveSection) => {
    if (disabled) return;
    onResponseTypeClick(section);
  };

  const handleOnManualUpload = (file?: File) => {
    if (file) {
      onManualUpload(file);

      handleOnClickResponseType(ActiveSection.VideoRecorder);
    }
  };

  const supportsMediaRecorder = useMemo(
    BrowserSupport.supportsMediaRecorder,
    []
  );
  const { t } = useTranslation();

  const recordResponseButtonProps = {
    className: 'w-full py-2',
    onClick: () => handleOnClickResponseType(ActiveSection.VideoRecorder),
    style: {
      borderRadius: `${ctaCornerRadius}px`,
      borderColor: primaryCtaColor,
      backgroundColor: primaryCtaColor,
      color: computeTextColor(primaryCtaColor),
      fontFamily
    }
  };

  const uploadVideoCtaTextColor = computeTextColor(secondaryCtaColor);
  const uploadVideoButtonProps = {
    variant: !supportsMediaRecorder ? ButtonVariant.Default : ButtonVariant.Secondary,
    className: 'w-full py-2 flex flex-row justify-center items-center',
    onFileSelected: handleOnManualUpload, 
    style: {
      borderRadius: `${ctaCornerRadius}px`,
      borderColor: secondaryCtaColor?.toLowerCase() !== theme.colors.white.toLowerCase() ? secondaryCtaColor : theme.colors.black,
      backgroundColor: secondaryCtaColor,
      color: uploadVideoCtaTextColor,
      fontFamily
    },
    disabled,
  };

  return (
    <div
      className={`max-w-md flex flex-col gap-y-[15px] justify-center items-center h-full m-auto ${className}`}
    >
      <p className='text-base font-bold' style={{color: fontColor, fontFamily}}>
        {t('testimonial:response.iWantTo')}
      </p>
      {supportsMediaRecorder && (
        <Button {...recordResponseButtonProps}>
          <div className='flex justify-center items-center'>
            <FontAwesomeIcon className='mr-2.5' icon={faVideo} />
            {t('testimonial:response.record')}
          </div>
        </Button>
      )}
      <ManualUploadButton {...uploadVideoButtonProps}>
        <UploadButtonIcon className='mr-3 icon icon-cloud-upload' color={uploadVideoCtaTextColor}/>
        {!supportsMediaRecorder
          ? t('testimonial:resopnserecordOrUpload')
          : t('testimonial:response.upload')}
      </ManualUploadButton>
      {!hideTextResponseOption && (
        <p
          role='button'
          className='text-sm text-turquoise-700 font-bold select-none'
          onClick={() => handleOnClickResponseType(ActiveSection.TextArea)}
          style={{
            color: fontColor,
            fontFamily
          }}
        >
          {t('testimonial:response.type')}
        </p>
      )}
    </div>
  );
}
